/**
 * First-touch attribution + internal-traffic exclusion.
 *
 * - `?nostat=1` marks the browser as internal (team/preview). Internal visits
 *   are never recorded as an attribution source, so bookings and concierge
 *   requests we generate ourselves don't pollute channel reporting.
 *   `?nostat=0` clears the flag.
 * - The FIRST visit that carries UTM params (or an external referrer) is
 *   stored and reused for every later submission in that browser.
 */

import { clarityEvent, clarityTag } from "@/lib/clarity";

const ATTRIBUTION_KEY = "amplify_attribution_v1";
const INTERNAL_KEY = "amplify_nostat";

export type Attribution = {
  source: string;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
  referrer: string | null;
  landing_path: string | null;
  first_seen_at: string;
  internal?: boolean;
};

const safeGet = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage disabled — attribution is best-effort */
  }
};

const safeRemove = (key: string) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* noop */
  }
};

export const isInternalTraffic = (): boolean => {
  if (typeof window === "undefined") return false;
  return safeGet(INTERNAL_KEY) === "1";
};

const classifyReferrer = (): { source: string; medium: string | null } => {
  const ref = typeof document !== "undefined" ? document.referrer : "";
  if (!ref) return { source: "direct", medium: null };
  let host = "";
  try {
    host = new URL(ref).hostname.replace(/^www\./, "");
  } catch {
    return { source: "direct", medium: null };
  }
  if (typeof window !== "undefined" && host === window.location.hostname.replace(/^www\./, "")) {
    return { source: "direct", medium: null };
  }
  const aiAssistants: Array<[string, string]> = [
    ["chatgpt.com", "chatgpt"],
    ["chat.openai.com", "chatgpt"],
    ["perplexity.ai", "perplexity"],
    ["claude.ai", "claude"],
    ["copilot.microsoft.com", "copilot"],
    ["gemini.google.com", "gemini"],
    ["you.com", "you.com"],
    ["phind.com", "phind"],
    ["meta.ai", "meta-ai"],
  ];
  const aiMatch = aiAssistants.find(([domain]) => host === domain || host.endsWith(`.${domain}`));
  if (aiMatch) return { source: "ai-assistant", medium: aiMatch[1] };
  const searchEngines = ["google.", "bing.", "duckduckgo.", "yahoo.", "ecosia.", "brave."];
  if (searchEngines.some((s) => host.includes(s))) return { source: host, medium: "organic" };
  const social = ["facebook.", "instagram.", "t.co", "twitter.", "x.com", "linkedin.", "pinterest.", "reddit.", "tiktok."];
  if (social.some((s) => host.includes(s))) return { source: host, medium: "social" };
  return { source: host, medium: "referral" };
};

const buildAttribution = (search: string, pathname: string): Attribution => {
  const params = new URLSearchParams(search);
  const fallback = classifyReferrer();
  return {
    source: (params.get("utm_source") || fallback.source).slice(0, 120),
    medium: (params.get("utm_medium") || fallback.medium || null)?.slice(0, 120) ?? null,
    campaign: params.get("utm_campaign")?.slice(0, 200) ?? null,
    content: params.get("utm_content")?.slice(0, 200) ?? null,
    term: params.get("utm_term")?.slice(0, 200) ?? null,
    referrer: (typeof document !== "undefined" ? document.referrer : "")?.slice(0, 500) || null,
    landing_path: pathname.slice(0, 300),
    first_seen_at: new Date().toISOString(),
  };
};

/** Preview / staging / local hosts are our own work sessions, never real visitors. */
const isInternalHost = (): boolean => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".lovable.app") ||
    host.endsWith(".lovable.dev") ||
    host.endsWith(".lovableproject.com")
  );
};

const recordInboundVisit = (attribution: Attribution): void => {
  void import("@/integrations/supabase/client").then(async ({ supabase }) => {
    // Skip our own team: signed-in admins and property managers are not inbound demand.
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (userId) {
        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId);
        if ((roles ?? []).some((r) => r.role === "admin" || r.role === "property_manager")) return;
      }
    } catch {
      /* role lookup is best-effort */
    }
    const { error } = await supabase.from("visit_events").insert({
      landing_path: attribution.landing_path ?? "/",
      referrer: attribution.referrer,
      source: attribution.source,
      medium: attribution.medium,
      user_agent: navigator.userAgent.slice(0, 500),
      first_seen_at: attribution.first_seen_at,
    });
    if (error) console.warn("Inbound visit measurement unavailable", error.message);
  });
};


/** Runs on every route change, stores first-touch attribution, and records the first public visit. */
export const captureAttribution = (search: string, pathname: string): void => {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(search);
  const nostat = params.get("nostat");
  if (nostat === "1") safeSet(INTERNAL_KEY, "1");
  if (nostat === "0") safeRemove(INTERNAL_KEY);
  if (isInternalTraffic() || isInternalHost()) return;

  const existing = safeGet(ATTRIBUTION_KEY);
  if (existing) return;

  const attribution = buildAttribution(search, pathname);
  safeSet(ATTRIBUTION_KEY, JSON.stringify(attribution));
  recordInboundVisit(attribution);
  // Tag Clarity sessions that arrived via an AI assistant so recordings and
  // heatmaps can be filtered against organic/social/direct traffic.
  if (attribution.source === "ai-assistant") {
    clarityTag("referral_source", attribution.medium ? `ai:${attribution.medium}` : "ai");
    clarityEvent("ai_referral");
  }
};

/** Attribution payload to attach to a submission, or null when unavailable. */
export const getAttribution = (): Attribution | null => {
  if (typeof window === "undefined") return null;
  if (isInternalTraffic()) {
    return {
      source: "internal",
      medium: "internal",
      campaign: null,
      content: null,
      term: null,
      referrer: null,
      landing_path: null,
      first_seen_at: new Date().toISOString(),
      internal: true,
    };
  }
  const raw = safeGet(ATTRIBUTION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Attribution;
  } catch {
    return null;
  }
};
